// Grupo compacto de promos del detalle de «Elige el teléfono»: caja con borde del color del grupo y
// una fila por promo (ícono, nombre, qué da en corto, chips a la derecha). Sin filas no se pinta.
import { useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
    PROMOTION_GROUP_LIMIT,
    PROMOTION_GROUP_TONE,
    type PromotionGroupTone,
} from '../promotionConstants';

/** Tonos del detalle: los de las promos y el azul informativo de «Sobre accesorios». */
export type CompactTone = PromotionGroupTone | 'accessory';

const BORDER: Record<CompactTone, string> = {
    gift: 'border-info/30',
    discount: 'border-success/30',
    benefit: 'border-warning/30',
    coupon: 'border-seg-slate/30',
    credit: 'border-promo/30',
    accessory: 'border-info/30',
};

function toneOf(tone: CompactTone): { box: string; text: string } {
    return tone === 'accessory' ? PROMOTION_GROUP_TONE.gift : PROMOTION_GROUP_TONE[tone];
}

export interface CompactPromotionItem {
    key: string;
    node: ReactNode;
}

export function CompactPromotionGroup({
    label,
    icon: Icon,
    tone,
    items,
    note,
    limit = PROMOTION_GROUP_LIMIT,
}: {
    label: string;
    icon: LucideIcon;
    tone: CompactTone;
    items: readonly CompactPromotionItem[];
    /** Aclaración junto al título, p. ej. «según su nivel». */
    note?: string | undefined;
    /** Cuántas se ven antes de «+N más». */
    limit?: number;
}) {
    const [expanded, setExpanded] = useState(false);
    if (items.length === 0) return null;
    const shown = expanded ? items : items.slice(0, limit);
    const hidden = items.length - shown.length;
    const colors = toneOf(tone);
    return (
        <section
            aria-label={label}
            className={cn('mt-3 rounded-md border px-3 py-2', colors.box, BORDER[tone])}
        >
            <div
                className={cn(
                    'mb-1 flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide',
                    colors.text,
                )}
            >
                <Icon aria-hidden className="size-3" />
                {label}
                {note && <span className="font-normal normal-case opacity-80">· {note}</span>}
            </div>
            <ul className="flex list-none flex-col text-[11.5px]">
                {shown.map((item) => (
                    <li key={item.key}>{item.node}</li>
                ))}
            </ul>
            {hidden > 0 && (
                <button
                    type="button"
                    onClick={() => {
                        setExpanded(true);
                    }}
                    className={cn(
                        'mt-0.5 rounded-sm text-[11px] font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        colors.text,
                    )}
                >
                    +{hidden} más
                </button>
            )}
        </section>
    );
}

/** Fila de una promo: nombre y qué da en corto (baja de línea si no cabe); chips o monto a la derecha. */
export function CompactPromotionRow({
    icon: Icon,
    tone,
    name,
    detail,
    aside,
}: {
    icon: LucideIcon;
    tone: CompactTone;
    name: string;
    /** Qué da, alcance y condiciones en texto corto. */
    detail?: string | undefined;
    aside?: ReactNode;
}) {
    const title = detail ? `${name} · ${detail}` : name;
    return (
        <div className="flex items-start gap-1.5 py-0.5 leading-snug" title={title}>
            <Icon aria-hidden className={cn('mt-0.5 size-3 flex-none', toneOf(tone).text)} />
            <span className="min-w-0 flex-1">
                <span className="text-foreground">{name}</span>
                {detail && <span className="text-muted-foreground"> · {detail}</span>}
            </span>
            {aside}
        </div>
    );
}
